<template>
  <v-edit-dialog :return-value.sync="inputValue" @close="onclose">
    <div :id="inputId">
      <span v-if="inputValue">{{ inputValue }}</span>
      <span v-else class="text--disabled">&mdash;</span>
    </div>
    <template v-slot:input>
      <div class="flex flex-col pt-3">
        <div class="timeContainer">
          <div class="hourAndMinute">
            <input
              :class="{ errorState: validateHour(hour) !== true }"
              v-model="hour"
              :id="`${inputId}-hour`"
              @input="onHourInput($event)"
              @keydown.tab.exact.prevent="() => handleTabPress(`${inputId}-minute`)"
              @keydown.shift.tab.prevent="handleShiftTabPress"
              :disabled="isTBA"
            />
            :
            <input
              :class="{ errorState: validateMinute(minute) !== true }"
              v-model="minute"
              :id="`${inputId}-minute`"
              @input="onMinInput($event)"
              @keydown.tab.exact.prevent="() => handleTabPress(`${inputId}-amOrPm`)"
              @keydown.shift.tab.prevent="() => handleTabPress(`${inputId}-hour`)"
              :disabled="isTBA"
            />
          </div>
          <v-select
            style="flex: 1"
            :id="`${inputId}-amOrPm`"
            :items="['AM', 'PM']"
            v-model="amOrPm"
            @keydown.shift.tab.prevent="() => handleTabPress(`${inputId}-minute`)"
            @keydown.tab.exact.prevent="handleTabPress"
            :disabled="isTBA"
          ></v-select>
        </div>
        <v-checkbox class="checkbox-tba" v-model="isTBA" @change="onTBAChange" label="TBA" color="black"></v-checkbox>
      </div>
    </template>
  </v-edit-dialog>
</template>

<script lang="ts">
  import { mixins } from '@/mixin/mixins';
  import Vue from 'vue';

  // Regex to match both "06:30 PM" and "7 p.m." formats
  const timeRegex = /^(?<hour>\d{1,2})(?::(?<minute>\d{1,2}))?\s*(?<amOrPm>[AP]\.?M\.?)$/i;

  export default Vue.extend({
    name: 'TimeInput',
    mixins: [mixins],
    props: {
      value: {
        type: String,
        default: '',
      },
      id: {
        type: String,
        default: '',
      },
      nextElementId: {
        type: String,
        default: '',
      },
      prevElementId: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        inputValue: this.value ?? '',
        inputId: this.id,
        hour: '12',
        minute: '00',
        amOrPm: 'AM',
        isTBA: false,
      };
    },
    mounted() {
      // In case TBA was set initially in previously versions
      if (this.inputValue.trim().toUpperCase() === 'TBA' || this.inputValue.trim().toUpperCase() === 'TBD') {
        this.hour = '';
        this.minute = '';
        this.amOrPm = '';
        this.isTBA = true;
        return;
      }

      // In case the text "Noon" was set initially in previously versions
      if (this.inputValue.trim().toUpperCase() === 'NOON') {
        this.hour = '12';
        this.minute = '00';
        this.amOrPm = 'PM';
        return;
      }

      const match = this.inputValue.trim().match(timeRegex);
      if (match) {
        const hour = parseInt(match.groups.hour);
        const minute = parseInt(match.groups.minute) ? parseInt(match.groups.minute) : 0;
        const amOrPm = match.groups.amOrPm.replace(/\./g, '').toLocaleUpperCase();
        if (hour < 1 || hour > 12) return;
        if (minute < 0 || minute > 59) return;
        this.hour = `${hour}`;
        this.minute = `${minute}`.padStart(2, '0');
        this.amOrPm = amOrPm;
        this.inputValue = `${this.hour}:${this.minute} ${amOrPm}`;
      }
    },
    watch: {
      inputValue(newVal) {
        this.$emit('input', newVal);
      },
    },
    methods: {
      onTBAChange($event: any) {
        if ($event) {
          this.hour = '';
          this.minute = '';
          this.amOrPm = '';
        } else {
          this.hour = '12';
          this.minute = '00';
          this.amOrPm = 'AM';
        }
      },
      onMinInput(e: Event) {
        const input = (e.target as HTMLInputElement).value;
        const str = input.replace(/^0+/, '').replace(/\D/g, '');
        if (str.length > 2) this.minute = str.substring(0, 2);
        else this.minute = str.padStart(2, '0');
      },
      onHourInput(e: Event) {
        const input = (e.target as HTMLInputElement).value;
        const str = input.replace(/^0+/, '').replace(/\D/g, '');
        if (str !== '') this.isTBA = false;
        if (str.length > 2) this.hour = str.substring(0, 2);
        else this.hour = str.padStart(2, '0');
      },
      validateHour(h: string) {
        if (h === '') return true;
        const hour = parseInt(h);
        return (hour >= 0 && hour <= 12) || 'Invalid hour';
      },
      validateMinute(m: string) {
        if (m === '') return true;
        const minute = parseInt(m);
        return (minute >= 0 && minute <= 59) || 'Invalid minute';
      },
      onclose() {
        if (this.validateHour(this.hour) !== true || this.validateMinute(this.minute) !== true) {
          const match = this.inputValue.match(timeRegex);
          if (match) {
            this.hour = match.groups.hour;
            this.minute = match.groups.minute;
            this.amOrPm = match.groups.amOrPm.toLocaleUpperCase();
          }
        } else if (this.hour === '' || this.minute === '') this.inputValue = 'TBA';
        else this.inputValue = `${this.hour}:${this.minute.padStart(2, '0')} ${this.amOrPm}`;
      },
      handleTabPress(to = '') {
        if (to) {
          this.clickElementbyId(to);
          return;
        }
        this.triggerEnterKeyPressEvent(`${this.inputId}-amOrPm`);
        this.clickElementbyId(this.nextElementId);
      },
      handleShiftTabPress() {
        this.triggerEnterKeyPressEvent(`${this.inputId}-hour`);
        this.clickElementbyId(this.prevElementId);
      },
    },
  });
</script>

<style scoped>
  .checkbox-tba {
    margin-top: 0px !important;
    padding-top: 0px !important;
  }
  input {
    /* Outline styling */
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    background-color: transparent;
    max-width: 3em;

    /* Dense styling */
    height: 2.5em;
    text-align: center;
    font-size: 1em;

    /* Typography */
    font-family: 'Roboto', sans-serif;
    color: rgba(0, 0, 0, 0.87);

    /* Remove default styles */
    outline: none;
  }

  /* Hover state */
  input:focus {
    border-color: rgba(0, 0, 0, 0.87);
    border-width: 2px;
  }

  /* Error state (for validation) */
  input.errorState {
    border-color: #ff5252;
    border-width: 2px;
  }

  .hourAndMinute {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .timeContainer {
    display: flex;
    align-items: center;
    max-width: 12em;
    gap: 1em;
  }
</style>
